import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewChild, inject, ChangeDetectorRef } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AncillaryMarkupItem, UpsertAncillaryMarkupRequest } from '@app/shared/interfaces/ancillary-markup.interface';
import { ColumnFilterComponent } from '@app/shared/components/column-filter/column-filter.component';
import { AncillaryService } from '@app/shared/services/ancillary.service';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { Table, TableModule } from 'primeng/table';
import { ToastModule } from 'primeng/toast';
import { ToggleSwitch } from 'primeng/toggleswitch';
import {
    AncillaryMarkupCreateEditComponent,
    AncillaryMarkupDialogMode,
    AncillaryMarkupFormModel
} from './ancillary-markup-create-edit/ancillary-markup-create-edit.component';
import { PageHeaderComponent } from '@app/shared/components/page-header/page-header.component';

@Component({
    selector: 'app-ancillary-markup-management',
    imports: [
        CommonModule,
        FormsModule,
        TableModule,
        ButtonModule,
        ProgressSpinnerModule,
        DialogModule,
        ToastModule,
        ToggleSwitch,
        ColumnFilterComponent,
        AncillaryMarkupCreateEditComponent,
        PageHeaderComponent
    ],
    templateUrl: './ancillary-markup-management.component.html',
    styleUrl: './ancillary-markup-management.component.scss'
})
export class AncillaryMarkupManagementComponent implements OnInit {
    @ViewChild('dt') dt!: Table;

    markups: AncillaryMarkupItem[] = [];
    loading = true;

    displayDialog = false;
    dialogMode: AncillaryMarkupDialogMode = 'add';
    dialogHeader = '';
    selected: AncillaryMarkupItem | null = null;
    dialogData: Partial<AncillaryMarkupFormModel> | null = null;

    private ancillaryService = inject(AncillaryService);
    private messageService = inject(MessageService);
    private cd = inject(ChangeDetectorRef);

    ngOnInit(): void {
        this.loadMarkups();
    }

    loadMarkups(): void {
        this.loading = true;
        this.cd.detectChanges();
        this.ancillaryService.getAncillaryMarkups().subscribe({
            next: (data) => {
                this.markups = data || [];
                this.loading = false;
                this.cd.detectChanges();
            },
            error: () => {
                this.loading = false;
                this.cd.detectChanges();
            }
        });
    }

    openAdd(): void {
        this.dialogMode = 'add';
        this.dialogHeader = 'Add New';
        this.selected = null;
        this.dialogData = null;
        this.displayDialog = true;
    }

    onEdit(row: AncillaryMarkupItem): void {
        this.dialogMode = 'edit';
        this.dialogHeader = 'Edit';
        this.selected = row;
        this.dialogData = this.mapToDialogData(row);
        this.displayDialog = true;
    }

    onDialogCancel(): void {
        this.displayDialog = false;
    }

    onDialogSave(model: AncillaryMarkupFormModel): void {
        const payload: UpsertAncillaryMarkupRequest = {
            name: model.name,
            price: model.price,
            providerId: model.providerId ?? undefined,
            active: model.active
        };

        if (this.dialogMode === 'add') {
            this.ancillaryService.createAncillaryMarkup(payload).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The item has been created successfully.' });
                    this.displayDialog = false;
                    this.loadMarkups();
                }
            });
        } else if (this.dialogMode === 'edit' && this.selected?.id) {
            this.ancillaryService.updateAncillaryMarkup(this.selected.id, payload).subscribe({
                next: () => {
                    this.messageService.add({ severity: 'success', summary: 'Success', detail: 'The item has been updated successfully.' });
                    this.displayDialog = false;
                    this.loadMarkups();
                }
            });
        }
    }

    onActiveToggle(row: AncillaryMarkupItem): void {
        if (!row?.id) return;

        const prev = !row.active;
        const next = row.active;

        this.ancillaryService.updateAncillaryMarkupStatus(row.id, next).subscribe({
            next: () => {
                this.messageService.add({
                    severity: 'success',
                    summary: 'Success',
                    detail: 'Status has been updated successfully.'
                });
            },
            error: () => {
                row.active = prev;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to update status.'
                });
            }
        });
    }

    private mapToDialogData(row: AncillaryMarkupItem): Partial<AncillaryMarkupFormModel> {
        return {
            id: row.id,
            name: row.name,
            providerId: row.providerId,
            price: row.price,
            active: row.active
        };
    }
}
